import { ORDER_TYPE } from '@exam-management/common/utils';
import { FilterOperator, QSetStatus, useGetInReviewQSetsQuery } from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import { Flex, type GridCellParams } from '@shared/components';
import { useMemo } from 'react';

import { BaseQSetList } from '../../components';
import { useQSetListSearchParams } from '../../hooks';

import { columns } from './columns';
import InReviewQSetListNoRowsOverlay from './in-review-qset-list-no-rows-overlay';

export default function InReviewQSetList() {
  const { courseId } = useParams('/exam-courses/:courseId/qsets');
  const navigate = useNavigate();
  const [
    { page, pageSize, sortOrder, sortBy },
    { changePageNumber, changePageSize, changeSorting },
  ] = useQSetListSearchParams();

  const { data: inReviewQSetsResult, loading: isSearchQSetsLoading } = useGetInReviewQSetsQuery({
    variables: {
      searchQSetsInput: {
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: sortBy,
        },
        filter: {
          conditions: [
            {
              value: courseId,
              operator: FilterOperator.Equal,
              field: 'examCourseId',
            },
            {
              value: QSetStatus.InReview,
              operator: FilterOperator.Equal,
              field: 'status',
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const totalQSets = inReviewQSetsResult?.excSearchQSets.total ?? 0;
  const qSets = useMemo(
    () =>
      (inReviewQSetsResult?.excSearchQSets.qSets ?? []).map((qSet) => ({
        id: qSet.id,
        code: qSet.code,
        status: qSet.status,
        updatedAt: new Date(qSet.updatedAt),
        lastReviewedAt: qSet.lastReviewedAt ? new Date(qSet.lastReviewedAt) : null,
        reviewers: qSet.assignees,
        qConfig: {
          code: qSet.qConfig.code,
          name: qSet.qConfig.name,
        },
      })),
    [inReviewQSetsResult?.excSearchQSets.qSets],
  );

  const handleCellClick = (cell: GridCellParams) => {
    if (!['moreAction', '__check__'].includes(cell.field)) {
      navigate('/exam-courses/:courseId/qsets/:qSetId', {
        params: {
          courseId,
          qSetId: cell.row.id,
        },
      });
    }
  };

  return (
    <Flex sx={{ flexDirection: 'column', flex: 1 }}>
      <BaseQSetList
        columns={columns}
        rows={qSets}
        getRowHeight={() => 'auto'}
        hideFooter={totalQSets === 0}
        loading={isSearchQSetsLoading}
        slots={{
          noRowsOverlay: InReviewQSetListNoRowsOverlay,
        }}
        onCellClick={handleCellClick}
        slotProps={{
          footer: {
            total: totalQSets,
            pagination: {
              page: page,
              count: totalQSets,
              rowsPerPage: pageSize,
              onPageChange: (_, page) => {
                changePageNumber(page);
              },
              onRowsPerPageChange: (event) => {
                changePageSize(parseInt(event.target.value));
              },
            },
          },
        }}
        onSortModelChange={(sorting) => {
          const sortModel = sorting[0];

          if (sortModel?.sort && sortModel?.field) {
            return changeSorting({
              sortOrder: sortModel.sort,
              sortBy: sortModel.field,
            });
          }
          // Reset sorting to default
          return changeSorting({
            sortOrder: null,
            sortBy: null,
          });
        }}
      />
    </Flex>
  );
}
